import { useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Redirect, useHistory, useLocation } from "react-router-dom";

import { OIDCAuthError, OIDCAuthLoading } from "./OidcComponents";
import { SessionContext } from "../../common/contexts/SessionContext";
import { genericEntityConstants, GenericEntityConstantsEntities } from "../../api/GenericConstants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { renderError } from "../../common/helperfunctions/ApiError";
import { useEntityApi } from "../../api/useEntityApi";
import { LoadingWrapper } from "../../common/LoadingWrapper";
import { Signature } from "../../api/Signature";

const actionIdKey = "actionId";
const returnUrlKey = "returnUrl";

/**
 * This component is shown at the /:group/oidc_redirect route. After logging in with OIDC, the user is redirected here,
 * We then redirect the user to the page they were trying to access before logging in, which we stored in localStorage.
 *
 * Another purpose of this component is to remove all URL params that are added by the OIDC provider. This happens
 * automatically due to the Redirect component rendered here.
 */
export const OidcRedirect = () => {
  const auth = useAuth();

  if (auth.isLoading) {
    return <OIDCAuthLoading />;
  }

  if (auth.error) {
    return <OIDCAuthError errorMessage={auth.error.message} />;
  }

  if (auth.isAuthenticated) {
    return <OIDCActionAndRedirectHandler />;
  }
  return <OIDCAuthError errorMessage="Unknown auth state" />;
};

export const useSigninRedirectWithAction = () => {
  const auth = useAuth();
  const { group } = useContext(SessionContext);
  const { createAsync } = useRedirectAction({ fetchActionId: false });

  const handleReauth = useCallback(
    async (redirectAction: Omit<RedirectAction, "id">) => {
      var response = await createAsync({ redirectAction }).catch((e) => console.error(e));
      if (!response) return;
      await auth
        .signinRedirect({
          redirect_uri: `${window.location.origin}/${group}/oidc_redirect?${actionIdKey}=${response.id}`,
          max_age: 0,
        })
        .catch((e) => console.error(e));
    },
    [auth, createAsync, group]
  );

  return { handleReauth };
};

const OIDCActionAndRedirectHandler = () => {
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const actionId = queryParams.get(actionIdKey);
  const returnUrl = queryParams.get(returnUrlKey) ?? localStorage.getItem(returnUrlKey) ?? "/";

  const { isRunning } = useRedirectAction({ actionId });

  if (!actionId) return <Redirect to={returnUrl} />;

  return (
    <LoadingWrapper status={isRunning ? "loading" : "success"} fetchStatus={isRunning ? "fetching" : "idle"}>
      {null}
    </LoadingWrapper>
  );
};

export const useReturnUrlStorage = () => {
  const location = useLocation();

  // We store the return URL in local storage so we can redirect the user back to the page they were trying to access
  useEffect(() => {
    !location.pathname.includes("oidc_redirect") &&
      localStorage.setItem("returnUrl", location.pathname + location.search);
  }, [location]);
};

// Redirect actions
export interface RedirectAction {
  id: string;
  entityType?: GenericEntityConstantsEntities;
  redirectUrl: string;
  entityIntIds?: number[];
  actions: Action[];
  signature?: Partial<Signature>;
}

type Action = "Unknown" | "Sign" | "Unlock";

export interface RedirectActionGetOptions {
  actionId: string;
}

export interface RedirectActionCreateOptions {
  redirectAction: Omit<RedirectAction, "id">;
}

export interface RedirectActionDeleteOptions {
  actionId: string;
}

const redirect_action_endpoint = "redirect_actions";

const useRedirectAction = ({
  actionId,
  fetchActionId = true,
}: {
  actionId?: RedirectActionGetOptions["actionId"] | null;
  fetchActionId?: boolean;
}) => {
  const { api } = useContext(SessionContext);
  const { replace } = useHistory();
  const queryClient = useQueryClient();
  const [isRunning, setIsRunning] = useState<boolean>(false);

  const createMutation = useMutation<RedirectAction, unknown, RedirectActionCreateOptions>(
    (options: RedirectActionCreateOptions) => {
      return api.post(`redirect_actions`, options.redirectAction);
    },
    {
      onSuccess: (response, variables) => {
        queryClient.invalidateQueries({ queryKey: [redirect_action_endpoint] });
      },
      onError: (err, variables) => {
        renderError(err);
      },
    }
  );

  const deleteMutation = useMutation<any, unknown, RedirectActionDeleteOptions>(
    (options: RedirectActionDeleteOptions) => {
      return api.delete(`redirect_actions/${options.actionId}`, {});
    },
    {
      onSuccess: (response, variables) => {
        queryClient.invalidateQueries({ queryKey: [redirect_action_endpoint] });
      },
      onError: (err, variables) => {
        renderError(err);
      },
    }
  );

  const { data: currentRedirectAction } = useQuery<RedirectAction>({
    queryKey: [redirect_action_endpoint, actionId],
    queryFn: async ({ signal }) => (actionId ? api?.get(`redirect_actions/${actionId}`, {}, signal) : null),
    enabled: !!fetchActionId && !!actionId,
  });
  var entityConstants = !!currentRedirectAction?.entityType
    ? genericEntityConstants[currentRedirectAction.entityType]
    : undefined;
  const { bulkSignMutationAsync, bulkUnlockMutationAsync } = useEntityApi(
    entityConstants?.entityConstants.resource ?? "announcements"
  );

  useEffect(() => {
    if (!!currentRedirectAction) {
      const runActions = async () => {
        if (isRunning) return; // Prevent multiple executions
        setIsRunning(true);
        currentRedirectAction.actions.forEach(async (action) => {
          try {
            switch (action) {
              case "Sign":
                await bulkSignMutationAsync({
                  ids: currentRedirectAction.entityIntIds ?? [],
                  params: { signature: currentRedirectAction.signature },
                  showToast: true,
                  entityName: entityConstants?.entityConstants.entitySingular,
                });
                break;
              case "Unlock":
                await bulkUnlockMutationAsync({
                  ids: currentRedirectAction.entityIntIds ?? [],
                  params: { signature: currentRedirectAction.signature },
                  showToast: true,
                  entityName: entityConstants?.entityConstants.entitySingular,
                });
                break;
              default:
                console.warn(`Unknown action: ${action} for redirect action ${currentRedirectAction.id}`);
            }
          } catch (error) {
            console.error(`Error executing action "${action}" for redirect action ${currentRedirectAction.id}:`, error);
          }
        });
        deleteMutation.mutateAsync({ actionId: currentRedirectAction.id }).catch((error) => {
          console.error(`Error deleting redirect action ${currentRedirectAction.id}:`, error);
        });
        setIsRunning(false);
        if (currentRedirectAction.redirectUrl) replace(currentRedirectAction.redirectUrl);
      };
      runActions();
    }
  }, [
    bulkSignMutationAsync,
    bulkUnlockMutationAsync,
    currentRedirectAction,
    deleteMutation,
    entityConstants?.entityConstants.entitySingular,
    isRunning,
    replace,
  ]);

  return {
    create: createMutation.mutate,
    createAsync: createMutation.mutateAsync,
    delete: deleteMutation.mutate,
    deleteAsync: deleteMutation.mutateAsync,
    currentRedirectAction,
    isRunning,
  };
};
